import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useHomam } from '../context/HomamContext';
import { getHomamExternalUrl, isHomamSubdomain } from '../utils/domain';
import '../styles/HomamAdPopup.css';

export function HomamAdPopup() {
  const { isHomamActive } = useHomam();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFloatingDismissed, setIsFloatingDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHomamRoute =
    location.pathname.startsWith('/nakshatra-homam') ||
    location.pathname.startsWith('/homam-') ||
    isHomamSubdomain();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    // If homam feature is disabled by admin, don't set timers
    if (!isHomamActive) return;

    // Check if floating banner was dismissed in this session
    const floatingDismissed = sessionStorage.getItem('homam_floating_dismissed') === 'true';
    if (floatingDismissed) {
      setIsFloatingDismissed(true);
    }

    // Check if modal was already shown and closed in this session
    const modalAlreadyShown = sessionStorage.getItem('homam_popup_shown') === 'true';
    if (!modalAlreadyShown) {
      // Show modal popup after site load - persistent until user manually exits
      const openTimer = setTimeout(() => {
        setIsModalOpen(true);
      }, 1500);

      return () => clearTimeout(openTimer);
    }
  }, [isHomamActive]);

  // Keyboard accessibility: Escape key manually exits popup modal
  useEffect(() => {
    if (!isModalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
        sessionStorage.setItem('homam_popup_shown', 'true');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  // Do not render anything on Nakshatra Homam pages, Admin portal, or if Homam is toggled OFF
  if (!isHomamActive || isHomamRoute || isAdminRoute) {
    return null;
  }

  const handleNavigateToHomam = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setIsModalOpen(false);
    sessionStorage.setItem('homam_popup_shown', 'true');

    const isProd = typeof window !== 'undefined' && window.location.hostname.includes('srignanasaibabamandir.com');

    if (location.pathname === '/nakshatra-homam') {
      const el = document.getElementById('find-star') || document.getElementById('booking-form');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }

    if (isProd) {
      window.location.href = getHomamExternalUrl('/#find-star');
    } else {
      navigate('/nakshatra-homam#find-star');
    }
  };

  const handleCloseModal = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setIsModalOpen(false);
    sessionStorage.setItem('homam_popup_shown', 'true');
  };

  const handleCloseFloating = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setIsFloatingDismissed(true);
    sessionStorage.setItem('homam_floating_dismissed', 'true');
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal(e);
    }
  };

  return (
    <>
      {/* 1. Sandal Brown Popup Banner (Center Modal - Persistent until closed via exit icon or Escape) */}
      {isModalOpen && (
        <div
          className="homam-ad-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-label="27 Nakshatra Homam Announcement"
        >
          <div
            className="homam-ad-modal-card"
            onClick={handleNavigateToHomam}
            title="Click to go to Homam Registration Form"
          >
            {/* Modal Close Cross Button */}
            <button
              type="button"
              className="homam-ad-close-btn"
              onClick={handleCloseModal}
              aria-label="Close Popup Banner (Esc)"
              title="Close Popup (Esc)"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Sandal Brown Poster Image */}
            <img
              src="/images/nakshatra_homam_popup.jpg"
              alt="Sri 27 Nakshatra Maha Homam - Sandal Brown Banner"
              className="homam-ad-modal-img"
              onError={(e) => {
                e.target.src = '/images/nakshatra_homam_ad.jpg';
              }}
            />

            {/* Glowing 11 Benefits Bar at bottom of popup */}
            <div className="homam-ad-modal-footer-bar">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: '#0a192f', background: '#f5c542', padding: '2px 8px', borderRadius: 10, flexShrink: 0 }}>
                  11 பலன்கள்
                </span>
                <span style={{ fontSize: 11.5, color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  அமைதி • நோய் நிவாரணம் • குடும்ப சுபிட்சம் • வெற்றி
                </span>
              </div>
              <span className="btn-cta-link" style={{ flexShrink: 0 }}>
                அறிய &rarr;
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 2. Blue Floating Banner (Bottom-Right Corner on all pages) */}
      {!isFloatingDismissed && (
        <div className="homam-ad-floating-container">
          <div
            className="homam-ad-floating-card"
            onClick={handleNavigateToHomam}
            title="Click to go to Homam Registration Form"
          >
            {/* Floating Banner Close Cross Button */}
            <button
              type="button"
              className="homam-ad-floating-close"
              onClick={handleCloseFloating}
              aria-label="Close Floating Banner"
              title="Close Floating Banner"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Blue Poster Image */}
            <img
              src="/images/nakshatra_homam_floating.jpg"
              alt="27 Nakshatra Homam - Blue Floating Banner"
              className="homam-ad-floating-img"
              onError={(e) => {
                e.target.src = '/images/nakshatra_homam_ad.jpg';
              }}
            />

            {/* Quick Benefits Highlight on Floating Card */}
            <div style={{
              background: 'linear-gradient(90deg, #0f2d5c 0%, #1e3c72 100%)',
              padding: '6px 10px',
              borderTop: '1px solid rgba(245, 197, 66, 0.4)',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4
            }}>
              <span style={{ fontSize: 10.5, color: '#fef08a', fontWeight: 800, letterSpacing: '0.2px' }}>
                ✨ 11 பலன்கள் & ஹோமப் பதிவு &rarr;
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HomamAdPopup;
